import {
  Component,
  h,
  Element,
  State,
  Prop,
  Watch,
  Event,
  EventEmitter,
  Method,
  Host,
} from "@stencil/core"
import {
  AccordionState,
  AccordionActions,
  accordionReducer,
  getInitialState,
} from "../../accordion-store"
import { Display } from "../../../../core/types/types"
import {
  StoreConnection,
  StateHandle,
  closest,
  animation,
} from "../../../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-accordion-item-body",
  styleUrl: "accordion-item-body.scss",
  shadow: true,
})
export class AccordionItemBody {
  private store: StoreConnection<this, AccordionState, AccordionActions>
  private animationDuration = 300
  private componentDidLoadComplete = false

  @Element() public el!: HTMLSdxAccordionItemBodyElement

  /**
   * @private
   * Disable animations for testing.
   */
  @Prop() public animated: boolean = true

  @State() public state!: StateHandle<AccordionState>

  @State() private display!: Display // will be set in the constructor

  /**
   * @private
   */
  @Prop() public componentStyle: Record<string, string | undefined> = {}

  @Watch("state")
  public stateChanged({}, prev: StateHandle<AccordionState>) {
    if (!this.componentDidLoadComplete) {
      return
    }

    const state = this.state.get()
    const prevState = prev.get()

    if (state.openAccordionItemEls !== prevState.openAccordionItemEls) {
      this.openAccordionItemElsChanged(state, prevState)
    }
  }

  /**
   * Emitted when the component’s visibility updates.
   */
  @Event() public sdxdisplaychange!: EventEmitter<{
    display: "open" | "opening" | "closed" | "closing"
  }>

  /**
   * Toggles body directly when initial load or with an animation.
   * @param isOpen Open state of the accordion item.
   * @private
   */
  @Method()
  public async toggle(isOpen: boolean) {
    if (isOpen) {
      this.store.dispatch({
        type: "OPEN_ACCORDION_ITEM_EL",
        accordionItemEl: this.getParent(),
      })
    } else {
      this.store.dispatch({
        type: "CLOSE_ACCORDION_ITEM_EL",
        accordionItemEl: this.getParent(),
      })
    }
  }

  constructor() {
    this.store = new StoreConnection(
      this,
      accordionReducer,
      getInitialState(),
      ["accordionItemEls", "openAccordionItemEls", "arrowPosition", "theme"],
    )
  }

  public componentWillLoad() {
    if (this.isOpen()) {
      this.open()
    } else {
      this.close()
    }
  }

  public componentDidLoad() {
    this.store.subscribe()

    this.componentDidLoadComplete = true
  }

  private getAnimationDuration(): number {
    if (!this.componentDidLoadComplete) {
      return 0
    }

    return this.animated ? this.animationDuration : 0
  }

  private setDisplay(
    display: Display,
    isUserInteractionInProgress: boolean,
  ): void {
    this.display = display

    // Only fire events on end user interaction
    if (!isUserInteractionInProgress) {
      return
    }

    this.sdxdisplaychange.emit({ display })
  }

  private openAccordionItemElsChanged({}, prevState: AccordionState) {
    const isOpen = this.isOpen()
    const wasOpen = this.isOpen(prevState.openAccordionItemEls)

    if (isOpen && !wasOpen) {
      this.open()
    } else if (!isOpen && wasOpen) {
      this.close()
    }
  }

  /**
   * Opens item with an animation.
   */
  private open(): void {
    const { isUserInteractionInProgress } = this.state.get()

    this.setDisplay("opening", isUserInteractionInProgress)

    // Shortly set the height to take measurements
    animation.set(this.el, { display: "block", height: "auto" })

    const clientHeight = this.el.clientHeight

    animation.set(this.el, { height: "0", opacity: "0" })

    animation.start(
      this.el,
      {
        height: `${clientHeight}px`,
        opacity: "1",
      },
      this.getAnimationDuration(),
      {
        finished: () => {
          animation.set(this.el, { height: "auto" })
          this.setDisplay("open", isUserInteractionInProgress)
        },
      },
    )
  }

  /**
   * Closes item with an animation.
   */
  private close() {
    const { isUserInteractionInProgress } = this.state.get()

    this.setDisplay("closing", isUserInteractionInProgress)

    animation.set(this.el, {
      height: `${this.el.clientHeight}px`,
      opacity: "1",
    })

    animation.start(
      this.el,
      { height: "0", opacity: "0" },
      this.getAnimationDuration(),
      {
        finished: () => {
          animation.set(this.el, { display: "none" })
          this.setDisplay("closed", isUserInteractionInProgress)
        },
      },
    )
  }

  private getParent(): HTMLSdxAccordionItemElement {
    const { accordionItemEls } = this.state.get()

    return accordionItemEls.find((el) => closest(this.el, el))!
  }

  private isOpen(
    openAccordionItemEls?: HTMLSdxAccordionItemElement[],
  ): boolean {
    if (!openAccordionItemEls) {
      openAccordionItemEls = this.state.get().openAccordionItemEls
    }

    return openAccordionItemEls.some((el) => closest(this.el, el))
  }

  private getComponentClassNames() {
    const { arrowPosition, theme, accordionItemEls } = this.state.get()

    return {
      component: true,
      [arrowPosition]: true,
      [theme]: true,
      "is-first-item": this.getParent() === accordionItemEls[0],
      "is-last-item":
        this.getParent() === accordionItemEls[accordionItemEls.length - 1],
    }
  }

  public render() {
    return (
      <Host aria-expanded={(this.display === "open").toString()}>
        <div class={this.getComponentClassNames()} style={this.componentStyle}>
          <slot />
        </div>
      </Host>
    )
  }
}
